import * as ents from '@attain-apps/entities'

import type * as meModels from '../../../models'

/**
 * Main App access inputs for app-scoped functionality.
 */
export interface AppAccess {
  readonly app: ents.core.admin.App.ForUser | undefined
  readonly tenant: meModels.Tenant | undefined
  readonly user: meModels.User | undefined
}

export namespace AppAccess {
  export interface WithScenario extends AppAccess {
    readonly scenario: ents.core.admin.Scenario | undefined
  }

  /**
   * Determines whether the current user can create scenarios in the app.
   */
  export function canCreateScenario({ app, tenant, user }: AppAccess): boolean {
    if (!canAccessScenarioApp(app)) {
      return false
    }

    return (
      user?.isPlatformAdmin === true ||
      canCreateScenarioChildren({ app, tenant, user })
    )
  }

  /**
   * Determines whether the current user can update the selected scenario.
   */
  export function canEditScenario({
    app,
    scenario,
    tenant,
    user,
  }: WithScenario): boolean {
    if (!scenario) {
      return false
    }

    if (!canAccessScenarioApp(app)) {
      return false
    }

    return (
      user?.isPlatformAdmin === true ||
      (scenario.createdBy === user?.id &&
        canCreateScenarioChildren({ app, tenant, user }))
    )
  }

  /**
   * Determines whether the current user can delete the selected scenario.
   */
  export function canDeleteScenario({
    app,
    scenario,
    tenant,
    user,
  }: WithScenario): boolean {
    if (!scenario) {
      return false
    }

    if (!canAccessScenarioApp(app)) {
      return false
    }

    if (user?.isPlatformAdmin === true) {
      return true
    }

    if (scenario.createdBy === user?.id) {
      return canCreateScenarioChildren({ app, tenant, user })
    }

    return canDeleteUnownedScenarioChildren({ app, tenant, user })
  }

  /**
   * Determines whether the current user can manage who has access to the
   * current tenant, which platform admins and tenant admins can.
   */
  export function canManageUsers({ tenant, user }: AppAccess): boolean {
    return (
      user?.isPlatformAdmin === true ||
      tenantHasAccess(tenant, ents.core.admin.Access.Flag.CanManageUsers)
    )
  }

  /**
   * Determines whether the current user can configure the current app's
   * functionality (e.g. Survey Configuration). This mirrors what the backend
   * enforces: the user needs to be able to use the app, and be either a
   * platform admin or explicitly granted functionality configuration.
   */
  export function canConfigureApp({ app, user }: AppAccess): boolean {
    return (
      appHasAccess(app, ents.core.admin.Access.Flag.CanUse) &&
      (user?.isPlatformAdmin === true ||
        appHasAccess(
          app,
          ents.core.admin.Access.Flag.CanConfigureFunctionality,
        ))
    )
  }

  /**
   * Determines whether the current app can be used for scenario management.
   */
  function canAccessScenarioApp(
    app: ents.core.admin.App.ForUser | undefined,
  ): boolean {
    return (
      appSupportsScenarios(app) &&
      appHasAccess(app, ents.core.admin.Access.Flag.CanUse)
    )
  }

  /**
   * Determines whether assignments allow managing user-owned scenarios.
   */
  function canCreateScenarioChildren({ app, tenant }: AppAccess): boolean {
    return (
      appHasAccess(app, ents.core.admin.Access.Flag.CanCreateChildren) ||
      tenantHasAccess(tenant, ents.core.admin.Access.Flag.CanCreateChildren)
    )
  }

  /**
   * Determines whether assignments allow deleting scenarios owned by others.
   */
  function canDeleteUnownedScenarioChildren({
    app,
    tenant,
  }: AppAccess): boolean {
    return (
      appHasAccess(app, ents.core.admin.Access.Flag.CanDeleteUnownedChildren) ||
      tenantHasAccess(
        tenant,
        ents.core.admin.Access.Flag.CanDeleteUnownedChildren,
      )
    )
  }

  /**
   * Determines whether the current user can mint API keys in the current zone.
   *
   * The flag is honored on the zone assignment or on the tenant assignment, so
   * a tenant-level grant covers every zone in that tenant. This mirrors what
   * the backend enforces — the UI only decides whether to offer the option.
   *
   * __NOTE:__ a zone is required even for a platform admin. Keys are bound to
   * exactly one zone, so off a zone route (tenant or home) there is nothing to
   * mint a key *for* — offering it there would open a panel that cannot load
   * or generate anything
   */
  export function canGenerateApiKey({
    tenant,
    user,
    zone,
  }: {
    readonly tenant: meModels.Tenant | undefined
    readonly user: meModels.User | undefined
    readonly zone: meModels.Zone | undefined
  }): boolean {
    if (!zone) {
      return false
    }

    return (
      user?.isPlatformAdmin === true ||
      ents.core.admin.Access.allows(
        zone.access,
        ents.core.admin.Access.Flag.CanGenerateApiKey,
      ) ||
      tenantHasAccess(tenant, ents.core.admin.Access.Flag.CanGenerateApiKey)
    )
  }

  /**
   * Determines whether the API Access panel should be reachable at all.
   *
   * Deliberately broader than {@link canGenerateApiKey}: the backend leaves
   * listing and revoking ungated so that a user whose permission was taken
   * away can still see and turn off keys they already hold. Hiding the entry
   * point on the generate permission alone would strand those credentials with
   * no way to revoke them from the UI
   */
  export function canAccessApiKeys({
    user,
    zone,
  }: {
    readonly user: meModels.User | undefined
    readonly zone: meModels.Zone | undefined
  }): boolean {
    if (!zone) {
      return false
    }

    return (
      user?.isPlatformAdmin === true ||
      ents.core.admin.Access.allows(
        zone.access,
        ents.core.admin.Access.Flag.CanUse,
      )
    )
  }

  /**
   * Determines whether the app supports Main App scenario management.
   */
  function appSupportsScenarios(
    app: ents.core.admin.App.ForUser | undefined,
  ): boolean {
    return app?.supportsScenarios === true
  }

  /**
   * Checks an app assignment for a specific access flag.
   */
  function appHasAccess(
    app: ents.core.admin.App.ForUser | undefined,
    flag: ents.core.admin.Access.Flag,
  ): boolean {
    return ents.core.admin.Access.allows(app?.access, flag)
  }

  /**
   * Checks a tenant assignment for a specific access flag.
   */
  function tenantHasAccess(
    tenant: meModels.Tenant | undefined,
    flag: ents.core.admin.Access.Flag,
  ): boolean {
    return ents.core.admin.Access.allows(tenant?.access, flag)
  }
}
